import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { useRootStore } from "../../../../store/contexts";
import { ShowMultiLine } from "../../../common/controls";
import { EmailVerificationStatus } from "../../../../api/graphql";
import { ShowString } from "../../../common/controls";
import { EmailVerificationScope } from "../../../../api/graphql";
import { ShowDateTime } from "../../../common/controls";
import { ShowNumber } from "../../../common/controls";
import { ShowReference } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type EmailVerificationViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  emailValue?: { displayId: string; id: string } | null;
  email: string;
  statusOptions: { id: string; displayId: string }[];
  status: EmailVerificationStatus;
  scopeOptions: { id: string; displayId: string }[];
  scope: EmailVerificationScope | "";
  expiresAtValue?: { displayId: string; id: string } | null;
  expiresAt: Date | null;
  sentAtValue?: { displayId: string; id: string } | null;
  sentAt: Date | null;
  verifiedAtValue?: { displayId: string; id: string } | null;
  verifiedAt: Date | null;
  sentCountValue?: { displayId: string; id: string } | null;
  sentCount: number;
  lastSentAtValue?: { displayId: string; id: string } | null;
  lastSentAt: Date | null;
  requestedByValue: { displayId: string; id: string } | null;
  sourceEntityValue?: { displayId: string; id: string } | null;
  sourceEntity: string;
  sourceRecordIdValue?: { displayId: string; id: string } | null;
  sourceRecordId: string;
  validationChangesValue?: { displayId: string; id: string } | null;
  validationChanges: string;
  validationSubmittedAtValue?: { displayId: string; id: string } | null;
  validationSubmittedAt: Date | null;
};

export const EmailVerificationViewForm: React.FC<EmailVerificationViewFormProps> =
  observer(
    ({
      onPrint = () => {},
      onCancel = () => {},
      onEdit,
      showToolbox = true,
      email = "",
      statusOptions = [],
      status = "" as EmailVerificationStatus,
      scopeOptions = [],
      scope = "" as EmailVerificationScope | "",
      expiresAt = null,
      sentAt = null,
      verifiedAt = null,
      sentCount = 0,
      lastSentAt = null,
      requestedByValue,
      sourceEntity = "",
      sourceRecordId = "",
      validationChanges = "{}",
      validationSubmittedAt = null,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      const componentRef = useRef(null);
      const handlePrint = useReactToPrint({
        content: () => componentRef.current,
      });

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              editForm={onEdit}
              printForm={() => onPrint(handlePrint)}
              closeForm={() => onCancel()}
            />
          )}
          <Box ref={componentRef}>
            <GroupContainer
              id="null"
              title={langStore.groupEmailVerificationLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <ShowMultiLine
                  value={email}
                  label={langStore.EmailVerificationEmailLabel}
                />
                <ShowString
                  value={
                    status
                      ? statusOptions.find(
                          (o: { id: string }) => o.id === status
                        )?.displayId || status
                      : ""
                  }
                  label={langStore.EmailVerificationStatusLabel}
                />
                <ShowString
                  value={
                    scope
                      ? scopeOptions.find((o: { id: string }) => o.id === scope)
                          ?.displayId || scope
                      : ""
                  }
                  label={langStore.EmailVerificationScopeLabel}
                />
                <ShowDateTime
                  value={expiresAt}
                  label={langStore.EmailVerificationExpiresAtLabel}
                />
                <ShowDateTime
                  value={sentAt}
                  label={langStore.EmailVerificationSentAtLabel}
                />
                <ShowDateTime
                  value={verifiedAt}
                  label={langStore.EmailVerificationVerifiedAtLabel}
                />
                <ShowNumber
                  value={sentCount}
                  label={langStore.EmailVerificationSentCountLabel}
                />
                <ShowDateTime
                  value={lastSentAt}
                  label={langStore.EmailVerificationLastSentAtLabel}
                />
                <ShowReference
                  label={langStore.EmailVerificationRequestedByLabel}
                  selectedValue={langStore.displayIdByLang(
                    requestedByValue?.displayId
                  )}
                />
                <ShowMultiLine
                  value={sourceEntity}
                  label={langStore.EmailVerificationSourceEntityLabel}
                />
                <ShowMultiLine
                  value={sourceRecordId}
                  label={langStore.EmailVerificationSourceRecordIdLabel}
                />
                <ShowMultiLine
                  value={validationChanges}
                  label={langStore.EmailVerificationValidationChangesLabel}
                />
                <ShowDateTime
                  value={validationSubmittedAt}
                  label={langStore.EmailVerificationValidationSubmittedAtLabel}
                />
              </FormControl>
            </GroupContainer>
          </Box>
        </Grid>
      );
    }
  );
